import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { QueryResult, RetrievedCard, BuildSessionListItem } from '@/lib/api'
import {
  listBuildSessions,
  createBuildSession,
  getBuildSessionWithTurns,
  deleteBuildSession as apiDeleteBuildSession,
  updateBuildSession,
} from '@/lib/api'
import { computeStreamingDiff, stripAnnotations } from '@/lib/diff'

export interface RagQuery {
  query: string
  reasoning: string
  priority: number
  target_card_types?: string[]
}

export interface Turn {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  thinking?: string  // Extended thinking content (collapsed after streaming)
}

export interface InterviewResponse {
  coaching_message: string
  question: string | null
  current_queries: RagQuery[]
  narrative_direction: string
  key_themes: string[]
  summary: string | null
  ready_for_retrieval: boolean
  session_id: string
  turn_count: number
  thinking?: string  // Extended thinking content
}

export interface InterviewSubmitResponse {
  message: string
  session_id: string
  event_id: string
  task_id: string
  workflow: string
  force_summary: boolean
}

type WorkflowStatus = 'idle' | 'submitting' | 'processing' | 'streaming' | 'complete' | 'error'
export type BuildMode = 'brainstorm' | 'foolscap' | 'draft'

export interface BrainstormStreamingContent {
  coaching_message: string
  question?: string | null
  brainstorm_content?: string
  cards_added?: string[]
  brainstorm_modified?: boolean
  session_context?: {
    phase?: string
    working_direction?: string
    key_themes?: string[]
    gaps?: string[]
  } | null
  // Extended thinking content from the model
  thinking?: string
}

export interface FoolscapStreamingContent {
  coaching_message: string
  question?: string | null
  foolscap_content?: string
  foolscap_modified?: boolean
  scratchpad_content?: string
  scratchpad_modified?: boolean
  session_context?: {
    phase?: string
    context_summary?: string
    structural_gaps?: string[]
  } | null
  // Finalized brainstorm content (annotations stripped) - sent on first foolscap turn
  finalized_brainstorm_content?: string
  // Extended thinking content from the model
  thinking?: string
}

interface InterviewState {
  // Session
  sessionId: string | null
  sessionName: string

  // Session list (for picker)
  sessions: BuildSessionListItem[]
  isLoadingSessions: boolean
  showSessionPicker: boolean

  // Build mode (brainstorm | foolscap | draft)
  buildMode: BuildMode

  // Conversation
  turns: Turn[]
  currentQueries: RagQuery[]
  narrativeDirection: string
  keyThemes: string[]
  phase: 'interviewing' | 'ready_for_retrieval'

  // Workflow status
  workflowStatus: WorkflowStatus
  currentTaskId: string | null
  error: string | null

  // Streaming state
  streamingContent: Partial<InterviewResponse> | null
  streamingThinking: string | null  // Extended thinking being streamed
  isThinking: boolean  // Whether currently streaming thinking

  // Retrieval results
  queryResults: QueryResult[]
  totalCards: number
  isRetrieving: boolean

  // Brainstorm state
  brainstormMode: boolean
  brainstormContent: string  // Annotated content for display (with ==additions== ~~deletions~~)
  brainstormCleanContent: string  // Clean content for next turn diff (no annotations)
  brainstormDisplayContent: string | null  // Transient display content during streaming (NOT persisted)
  brainstormPhase: string
  brainstormWorkingDirection: string
  brainstormKeyThemes: string[]
  brainstormGaps: string[]
  brainstormTurns: Turn[]
  brainstormStreamingContent: BrainstormStreamingContent | null
  brainstormStreamingThinking: string | null
  brainstormIsThinking: boolean
  // Streaming document state
  brainstormStreamingDocument: string  // Accumulating streamed document lines
  brainstormDocumentStreaming: boolean  // Whether document is currently streaming
  brainstormDocumentPending: boolean  // Whether document update is pending
  brainstormStreamingLineCount: number  // Current line count for progress indicator

  // Foolscap state
  foolscapContent: string
  foolscapCleanContent: string  // Clean content for next turn diff (no annotations)
  foolscapDisplayContent: string | null  // Transient display content during streaming (NOT persisted)
  foolscapPhase: string
  foolscapStreamingContent: FoolscapStreamingContent | null
  foolscapStreamingThinking: string | null
  foolscapIsThinking: boolean
  // Streaming document state
  foolscapStreamingDocument: string  // Accumulating streamed document lines
  foolscapDocumentStreaming: boolean  // Whether document is currently streaming
  foolscapDocumentPending: boolean
  foolscapStreamingLineCount: number  // Current line count for progress indicator

  // Scratchpad state (agent's working notes)
  scratchpadContent: string
  scratchpadCleanContent: string  // Clean content for next turn diff (no annotations)
  scratchpadDisplayContent: string | null  // Transient display content during streaming (NOT persisted)
  // Streaming scratchpad state
  scratchpadStreamingDocument: string  // Accumulating streamed scratchpad lines
  scratchpadDocumentStreaming: boolean  // Whether scratchpad is currently streaming
  scratchpadStreamingLineCount: number  // Current line count for progress indicator

  // Actions
  setBuildMode: (mode: BuildMode) => void
  setSessionName: (name: string) => void
  addUserTurn: (content: string) => void
  addAssistantTurn: (response: InterviewResponse) => void
  setWorkflowStatus: (status: WorkflowStatus) => void
  setCurrentTaskId: (taskId: string | null) => void
  setError: (error: string | null) => void
  startNewSession: () => void
  loadSession: (sessionId: string, turns: Turn[], queries: RagQuery[], direction: string, themes: string[]) => void

  // Session management actions
  openSessionPicker: () => void
  closeSessionPicker: () => void
  fetchSessions: () => Promise<void>
  createNewSession: (name?: string) => Promise<void>
  loadSessionFromDb: (sessionId: string) => Promise<void>
  deleteSession: (sessionId: string) => Promise<void>
  renameSession: (name: string) => Promise<void>

  // Streaming actions
  startStreaming: () => void
  updateStreamingContent: (chunk: Partial<InterviewResponse>) => void
  updateStreamingThinking: (delta: string) => void
  startThinking: () => void
  finishThinking: () => void
  commitStreamingMessage: () => void  // Convert streaming to real message, unlock input
  finishStreaming: (response: InterviewResponse) => void

  // Retrieval actions
  setIsRetrieving: (isRetrieving: boolean) => void
  setQueryResults: (results: QueryResult[], total: number) => void
  clearQueryResults: () => void

  // Brainstorm actions
  enterBrainstormMode: () => void
  exitBrainstormMode: () => void
  updateBrainstormContent: (content: string) => void
  addBrainstormTurn: (role: 'user' | 'assistant', content: string) => void
  addBrainstormUserTurn: (content: string) => void
  setBrainstormPhase: (phase: string) => void
  setBrainstormWorkingDirection: (direction: string) => void
  setBrainstormKeyThemes: (themes: string[]) => void
  setBrainstormGaps: (gaps: string[]) => void

  // Brainstorm streaming actions
  startBrainstormStreaming: () => void
  updateBrainstormStreamingContent: (chunk: Partial<BrainstormStreamingContent>) => void
  setBrainstormCoachingMessage: (message: string) => void  // SET (not append) for complete messages
  startBrainstormThinking: () => void
  updateBrainstormThinking: (delta: string) => void
  finishBrainstormThinking: () => void
  commitBrainstormStreamingMessage: () => void
  finishBrainstormStreaming: (output: BrainstormStreamingContent & { new_brainstorm_content?: string }) => void
  // Document streaming - line-by-line streaming with frontend diff computation
  startBrainstormDocumentStreaming: () => void
  appendBrainstormDocumentLine: (line: string) => void
  finishBrainstormDocumentStreaming: (fullDocument: string) => void
  // Legacy: Document update - receives complete annotated document from backend
  updateBrainstormDocumentInstant: (content: string) => void

  // Foolscap actions
  updateFoolscapContent: (content: string) => void
  startFoolscapStreaming: () => void
  updateFoolscapStreamingContent: (chunk: Partial<FoolscapStreamingContent>) => void
  setFoolscapCoachingMessage: (message: string) => void  // SET (not append) for complete messages
  startFoolscapThinking: () => void
  updateFoolscapThinking: (delta: string) => void
  finishFoolscapThinking: () => void
  commitFoolscapStreamingMessage: () => void
  finishFoolscapStreaming: (output: FoolscapStreamingContent & { new_foolscap_content?: string; new_scratchpad_content?: string }) => void
  // Document streaming - line-by-line streaming with frontend diff computation
  startFoolscapDocumentStreaming: () => void
  appendFoolscapDocumentLine: (line: string) => void
  finishFoolscapDocumentStreaming: (fullDocument: string) => void
  // Legacy: Document update - receives complete annotated document from backend
  updateFoolscapDocumentInstant: (content: string) => void

  // Scratchpad streaming actions
  updateScratchpadContent: (content: string) => void
  startScratchpadDocumentStreaming: () => void
  appendScratchpadDocumentLine: (line: string) => void
  finishScratchpadDocumentStreaming: (fullScratchpad: string) => void
}

export const useInterviewStore = create<InterviewState>()(
  persist(
    (set) => ({
      // Initial state
      sessionId: null,
      sessionName: 'New Session',
      sessions: [],
      isLoadingSessions: false,
      showSessionPicker: false,
      buildMode: 'brainstorm',
      turns: [],
      currentQueries: [],
      narrativeDirection: '',
      keyThemes: [],
      phase: 'interviewing',
      workflowStatus: 'idle',
      currentTaskId: null,
      error: null,

      // Streaming state
      streamingContent: null,
      streamingThinking: null,
      isThinking: false,

      // Retrieval state
      queryResults: [],
      totalCards: 0,
      isRetrieving: false,

      // Brainstorm state - always in brainstorm mode by default
      brainstormMode: true,
      brainstormContent: '',
      brainstormCleanContent: '',
      brainstormDisplayContent: null,  // Transient - not persisted
      brainstormPhase: 'direction_finding',
      brainstormWorkingDirection: '',
      brainstormKeyThemes: [],
      brainstormGaps: [],
      brainstormTurns: [],
      brainstormStreamingContent: null,
      brainstormStreamingThinking: null,
      brainstormIsThinking: false,
      // Streaming document state
      brainstormStreamingDocument: '',
      brainstormDocumentStreaming: false,
      brainstormDocumentPending: false,
      brainstormStreamingLineCount: 0,

      // Foolscap state
      foolscapContent: '',
      foolscapCleanContent: '',
      foolscapDisplayContent: null,  // Transient - not persisted
      foolscapPhase: 'draft_creation',
      foolscapStreamingContent: null,
      foolscapStreamingThinking: null,
      foolscapIsThinking: false,
      // Streaming document state
      foolscapStreamingDocument: '',
      foolscapDocumentStreaming: false,
      foolscapDocumentPending: false,
      foolscapStreamingLineCount: 0,

      // Scratchpad state
      scratchpadContent: '',
      scratchpadCleanContent: '',
      scratchpadDisplayContent: null,  // Transient - not persisted
      // Streaming scratchpad state
      scratchpadStreamingDocument: '',
      scratchpadDocumentStreaming: false,
      scratchpadStreamingLineCount: 0,

      // Actions
      setBuildMode: (mode) => set({ buildMode: mode }),
      setSessionName: (name) => set({ sessionName: name }),

      addUserTurn: (content) =>
        set((state) => ({
          turns: [
            ...state.turns,
            {
              role: 'user',
              content,
              timestamp: new Date().toISOString(),
            },
          ],
        })),

      addAssistantTurn: (response) =>
        set((state) => ({
          sessionId: response.session_id,
          turns: [
            ...state.turns,
            {
              role: 'assistant',
              content: response.coaching_message,
              timestamp: new Date().toISOString(),
            },
          ],
          currentQueries: response.current_queries,
          narrativeDirection: response.narrative_direction,
          keyThemes: response.key_themes,
          phase: response.ready_for_retrieval ? 'ready_for_retrieval' : 'interviewing',
          workflowStatus: 'idle',
        })),

      setWorkflowStatus: (status) => set({ workflowStatus: status }),

      setCurrentTaskId: (taskId) => set({ currentTaskId: taskId }),

      setError: (error) => set({ error, workflowStatus: 'error' }),

      startNewSession: () =>
        set({
          sessionId: null,
          sessionName: 'New Session',
          buildMode: 'brainstorm',
          turns: [],
          currentQueries: [],
          narrativeDirection: '',
          keyThemes: [],
          phase: 'interviewing',
          workflowStatus: 'idle',
          currentTaskId: null,
          error: null,
          streamingContent: null,
          streamingThinking: null,
          isThinking: false,
          queryResults: [],
          totalCards: 0,
          isRetrieving: false,
          // Reset brainstorm state - always in brainstorm mode now
          brainstormMode: true,
          brainstormContent: '',
          brainstormCleanContent: '',
          brainstormDisplayContent: null,
          brainstormPhase: 'direction_finding',
          brainstormWorkingDirection: '',
          brainstormKeyThemes: [],
          brainstormGaps: [],
          brainstormTurns: [],
          brainstormStreamingContent: null,
          brainstormStreamingThinking: null,
          brainstormIsThinking: false,
          brainstormStreamingDocument: '',
          brainstormDocumentStreaming: false,
          brainstormDocumentPending: false,
          brainstormStreamingLineCount: 0,
          // Reset foolscap state
          foolscapContent: '',
          foolscapPhase: 'draft_creation',
          foolscapStreamingContent: null,
          foolscapStreamingThinking: null,
          foolscapIsThinking: false,
          foolscapDocumentPending: false,
          // Reset scratchpad state
          scratchpadContent: '',
        }),

      loadSession: (sessionId, turns, queries, direction, themes) =>
        set({
          sessionId,
          turns,
          currentQueries: queries,
          narrativeDirection: direction,
          keyThemes: themes,
          phase: 'interviewing',
          workflowStatus: 'idle',
          error: null,
        }),

      // Session management actions
      openSessionPicker: () => set({ showSessionPicker: true }),
      closeSessionPicker: () => set({ showSessionPicker: false }),

      fetchSessions: async () => {
        set({ isLoadingSessions: true })
        try {
          const sessions = await listBuildSessions('active', 50, 0)
          set({ sessions, isLoadingSessions: false })
        } catch (error) {
          console.error('Failed to fetch sessions:', error)
          set({ isLoadingSessions: false })
        }
      },

      createNewSession: async (name?: string) => {
        try {
          const session = await createBuildSession(name, 'brainstorm')
          set({
            sessionId: session.session_id,
            sessionName: session.name,
            buildMode: session.mode as BuildMode,
            turns: [],
            currentQueries: [],
            narrativeDirection: '',
            keyThemes: [],
            phase: 'interviewing',
            workflowStatus: 'idle',
            brainstormMode: true,
            brainstormContent: session.brainstorm_content || '',
            brainstormCleanContent: '',  // Reset for new session - prevents diff against old session
            brainstormDisplayContent: null,
            brainstormTurns: [],
            showSessionPicker: false,
            // Clear documents from previous session
            foolscapContent: '',
            foolscapCleanContent: '',  // Reset for new session - prevents diff against old session
            foolscapDisplayContent: null,
            foolscapPhase: 'draft_creation',
            scratchpadContent: '',
            scratchpadCleanContent: '',  // Reset for new session
            scratchpadDisplayContent: null,
            brainstormKeyThemes: [],
            brainstormGaps: [],
          })
        } catch (error) {
          console.error('Failed to create session:', error)
        }
      },

      loadSessionFromDb: async (sessionId: string) => {
        try {
          const session = await getBuildSessionWithTurns(sessionId)
          // Convert DB turns to store Turn format
          const turns: Turn[] = session.turns.map((t) => ({
            role: t.role,
            content: t.content,
            timestamp: t.timestamp,
            thinking: t.thinking || undefined,
          }))
          // For diff computation: use new_*_content if available, otherwise strip annotations
          const cleanContent = session.new_brainstorm_content || stripAnnotations(session.brainstorm_content || '')
          const foolscapCleanContent = session.new_foolscap_content || stripAnnotations(session.foolscap_content || '')
          const scratchpadCleanContent = session.new_scratchpad_content || stripAnnotations(session.scratchpad_content || '')
          // Extract themes/gaps from session_context if available
          const sessionContext = session.session_context || {}
          const keyThemes: string[] = Array.isArray(sessionContext.key_themes) ? sessionContext.key_themes : []
          const gaps: string[] = Array.isArray(sessionContext.gaps) ? sessionContext.gaps : []

          set({
            sessionId: session.session_id,
            sessionName: session.name,
            buildMode: session.mode as BuildMode,
            turns: [],
            brainstormTurns: turns,
            brainstormContent: session.brainstorm_content || '',
            brainstormCleanContent: cleanContent,  // For next turn diff computation
            brainstormMode: true,
            phase: 'interviewing',
            workflowStatus: 'idle',
            showSessionPicker: false,
            // Load foolscap content if available
            foolscapContent: session.foolscap_content || '',
            foolscapCleanContent: foolscapCleanContent,  // For next turn diff computation
            foolscapPhase: session.foolscap_content ? 'refinement' : 'draft_creation',
            // Load scratchpad content if available
            scratchpadContent: session.scratchpad_content || '',
            scratchpadCleanContent: scratchpadCleanContent,  // For next turn diff computation
            // Reset themes/gaps from session_context (prevent stale data from other sessions)
            brainstormKeyThemes: keyThemes,
            brainstormGaps: gaps,
          })
        } catch (error) {
          console.error('Failed to load session:', error)
        }
      },

      deleteSession: async (sessionId: string) => {
        try {
          await apiDeleteBuildSession(sessionId)
          set((state) => ({
            sessions: state.sessions.filter((s) => s.session_id !== sessionId),
          }))
        } catch (error) {
          console.error('Failed to delete session:', error)
        }
      },

      renameSession: async (name: string) => {
        const { sessionId } = useInterviewStore.getState()
        if (!sessionId) {
          // No session in DB yet, just update local state
          set({ sessionName: name })
          return
        }
        try {
          await updateBuildSession(sessionId, { name })
          set({ sessionName: name })
        } catch (error) {
          console.error('Failed to rename session:', error)
        }
      },

      // Streaming actions
      startStreaming: () =>
        set({
          workflowStatus: 'streaming',
          streamingContent: null,
          streamingThinking: null,
          isThinking: false,
        }),

      startThinking: () =>
        set({
          isThinking: true,
          streamingThinking: '',
        }),

      updateStreamingThinking: (delta) =>
        set((state) => ({
          streamingThinking: (state.streamingThinking || '') + delta,
        })),

      finishThinking: () =>
        set({
          isThinking: false,
        }),

      updateStreamingContent: (chunk) =>
        set((state) => {
          const current = state.streamingContent || {}
          // Accumulate coaching_message deltas for character-by-character streaming
          const newCoachingMessage = chunk.coaching_message
            ? (current.coaching_message || '') + chunk.coaching_message
            : current.coaching_message
          return {
            streamingContent: {
              ...current,
              ...chunk,
              coaching_message: newCoachingMessage,
            },
          }
        }),

      // Convert streaming message to real turn, unlock input while JSON finalizes
      commitStreamingMessage: () =>
        set((state) => {
          const content = state.streamingContent?.coaching_message || ''
          if (!content) return state
          return {
            turns: [
              ...state.turns,
              {
                role: 'assistant' as const,
                content,
                timestamp: new Date().toISOString(),
                thinking: state.streamingThinking || undefined,
              },
            ],
            workflowStatus: 'idle',
            streamingContent: null,
            streamingThinking: null,
            isThinking: false,
          }
        }),

      finishStreaming: (response) =>
        set((state) => {
          // Check if message was already committed (streamingContent is null)
          const alreadyCommitted = !state.streamingContent

          // If already committed, just update metadata without adding duplicate turn
          if (alreadyCommitted) {
            return {
              sessionId: response.session_id,
              currentQueries: response.current_queries,
              narrativeDirection: response.narrative_direction,
              keyThemes: response.key_themes,
              phase: response.ready_for_retrieval ? 'ready_for_retrieval' : 'interviewing',
              workflowStatus: 'idle',
              streamingThinking: null,
              isThinking: false,
            }
          }

          // Otherwise, add the turn and update everything
          return {
            sessionId: response.session_id,
            turns: [
              ...state.turns,
              {
                role: 'assistant' as const,
                content: response.coaching_message,
                timestamp: new Date().toISOString(),
                thinking: response.thinking || state.streamingThinking || undefined,
              },
            ],
            currentQueries: response.current_queries,
            narrativeDirection: response.narrative_direction,
            keyThemes: response.key_themes,
            phase: response.ready_for_retrieval ? 'ready_for_retrieval' : 'interviewing',
            workflowStatus: 'idle',
            streamingContent: null,
            streamingThinking: null,
            isThinking: false,
          }
        }),

      // Retrieval actions
      setIsRetrieving: (isRetrieving) => set({ isRetrieving }),

      setQueryResults: (results, total) =>
        set({
          queryResults: results,
          totalCards: total,
          isRetrieving: false,
        }),

      clearQueryResults: () =>
        set({
          queryResults: [],
          totalCards: 0,
        }),

      // Brainstorm actions
      enterBrainstormMode: () =>
        set((state) => ({
          brainstormMode: true,
          brainstormContent: '',
          brainstormPhase: 'direction_finding',
          brainstormWorkingDirection: state.narrativeDirection,
          brainstormKeyThemes: state.keyThemes,
          brainstormGaps: [],
          brainstormTurns: [],
          brainstormStreamingContent: null,
        })),

      exitBrainstormMode: () =>
        set({
          brainstormMode: false,
          brainstormContent: '',
          brainstormPhase: 'direction_finding',
          brainstormWorkingDirection: '',
          brainstormKeyThemes: [],
          brainstormGaps: [],
          brainstormTurns: [],
          brainstormStreamingContent: null,
        }),

      updateBrainstormContent: (content) =>
        set({ brainstormContent: content }),

      addBrainstormTurn: (role, content) =>
        set((state) => ({
          brainstormTurns: [
            ...state.brainstormTurns,
            {
              role,
              content,
              timestamp: new Date().toISOString(),
            },
          ],
        })),

      setBrainstormPhase: (phase) =>
        set({ brainstormPhase: phase }),

      setBrainstormWorkingDirection: (direction) =>
        set({ brainstormWorkingDirection: direction }),

      setBrainstormKeyThemes: (themes) =>
        set({ brainstormKeyThemes: themes }),

      setBrainstormGaps: (gaps) =>
        set({ brainstormGaps: gaps }),

      addBrainstormUserTurn: (content) =>
        set((state) => ({
          brainstormTurns: [
            ...state.brainstormTurns,
            {
              role: 'user' as const,
              content,
              timestamp: new Date().toISOString(),
            },
          ],
        })),

      // Brainstorm streaming actions
      startBrainstormStreaming: () =>
        set({
          workflowStatus: 'streaming',
          brainstormStreamingContent: { coaching_message: '' },  // Initialize with empty object to track streaming state
          brainstormStreamingThinking: null,
          brainstormIsThinking: false,
        }),

      updateBrainstormStreamingContent: (chunk) =>
        set((state) => {
          const current = state.brainstormStreamingContent || { coaching_message: '' }
          const newCoachingMessage = chunk.coaching_message
            ? (current.coaching_message || '') + chunk.coaching_message
            : current.coaching_message
          return {
            brainstormStreamingContent: {
              ...current,
              ...chunk,
              coaching_message: newCoachingMessage,
            },
          }
        }),

      // SET (not append) coaching message - for complete messages from backend
      setBrainstormCoachingMessage: (message) =>
        set((state) => ({
          brainstormStreamingContent: {
            ...state.brainstormStreamingContent,
            coaching_message: message,
          },
        })),

      startBrainstormThinking: () =>
        set({
          brainstormIsThinking: true,
          brainstormStreamingThinking: '',
        }),

      updateBrainstormThinking: (delta) =>
        set((state) => ({
          brainstormStreamingThinking: (state.brainstormStreamingThinking || '') + delta,
        })),

      finishBrainstormThinking: () =>
        set({
          brainstormIsThinking: false,
        }),

      commitBrainstormStreamingMessage: () =>
        set((state) => {
          const content = state.brainstormStreamingContent?.coaching_message || ''
          if (!content) return state
          return {
            brainstormTurns: [
              ...state.brainstormTurns,
              {
                role: 'assistant' as const,
                content,
                thinking: state.brainstormStreamingThinking || undefined,
                timestamp: new Date().toISOString(),
              },
            ],
            workflowStatus: 'idle',
            brainstormStreamingContent: null,
            brainstormStreamingThinking: null,
            brainstormIsThinking: false,
            // Document update is still pending
            brainstormDocumentPending: true,
          }
        }),

      finishBrainstormStreaming: (output) =>
        set((state) => {
          const alreadyCommitted = !state.brainstormStreamingContent
          const sessionContext = output.session_context || {}

          // Sync transient display content to persisted content
          // Priority: displayContent (from streaming) > existing content > output content
          const contentToUse = state.brainstormDisplayContent
            || (state.brainstormContent && state.brainstormContent.length > 0 ? state.brainstormContent : null)
            || output.brainstorm_content
            || ''

          console.log('[store] finishBrainstormStreaming')
          console.log('[store] state.brainstormDisplayContent length:', state.brainstormDisplayContent?.length)
          console.log('[store] state.brainstormContent length:', state.brainstormContent?.length)
          console.log('[store] contentToUse length:', contentToUse.length)
          console.log('[store] Has annotations:', contentToUse.includes('==') || contentToUse.includes('~~'))

          const updates: Partial<InterviewState> = {
            // Sync display content to persisted content (safe to persist now)
            brainstormContent: contentToUse,
            // Clear transient display content
            brainstormDisplayContent: null,
            // Store clean content for next turn's diff computation
            brainstormCleanContent: output.new_brainstorm_content || stripAnnotations(contentToUse),
            brainstormPhase: sessionContext.phase || state.brainstormPhase,
            brainstormWorkingDirection: sessionContext.working_direction || state.brainstormWorkingDirection,
            brainstormKeyThemes: sessionContext.key_themes || state.brainstormKeyThemes,
            brainstormGaps: sessionContext.gaps || state.brainstormGaps,
            workflowStatus: 'idle',
            brainstormStreamingContent: null,
            brainstormDocumentPending: false,
            brainstormDocumentStreaming: false,
          }

          // If message was already committed but we have a question, update the last turn
          if (alreadyCommitted && output.question) {
            const updatedTurns = [...state.brainstormTurns]
            const lastTurn = updatedTurns[updatedTurns.length - 1]
            if (lastTurn && lastTurn.role === 'assistant') {
              updatedTurns[updatedTurns.length - 1] = {
                ...lastTurn,
                content: `${lastTurn.content}\n\n${output.question}`,
              }
            }
            return {
              ...updates,
              brainstormTurns: updatedTurns,
            }
          }

          if (alreadyCommitted) {
            return updates
          }

          // Include question in the message content if present
          const messageContent = output.question
            ? `${output.coaching_message}\n\n${output.question}`
            : output.coaching_message

          return {
            ...updates,
            brainstormTurns: [
              ...state.brainstormTurns,
              {
                role: 'assistant' as const,
                content: messageContent,
                timestamp: new Date().toISOString(),
                // Prefer thinking from output (complete), fall back to streaming thinking
                thinking: output.thinking || state.brainstormStreamingThinking || undefined,
              },
            ],
            // Clear streaming thinking now that it's committed to turn
            brainstormStreamingThinking: null,
          }
        }),

      // Document streaming actions - line-by-line with frontend diff computation
      // Key insight: Keep showing the OLD document during streaming, only show diff at the END
      startBrainstormDocumentStreaming: () => {
        console.log('[store] startBrainstormDocumentStreaming')
        set({
          brainstormDocumentStreaming: true,
          brainstormDocumentPending: true,
          brainstormStreamingDocument: '',
          brainstormStreamingLineCount: 0,
          // DON'T clear brainstormContent - keep showing existing document
        })
      },

      appendBrainstormDocumentLine: (line) => {
        const state = useInterviewStore.getState()
        const newStreamingDoc = state.brainstormStreamingDocument + line
        const newLineCount = state.brainstormStreamingLineCount + 1

        console.log('[store] appendBrainstormDocumentLine, line:', newLineCount, 'total length:', newStreamingDoc.length)

        // Compute diff of accumulated content vs old content - update display on EVERY line
        // Pass isComplete=false to avoid marking remaining old lines as deleted prematurely
        const annotatedContent = computeStreamingDiff(
          state.brainstormCleanContent,  // Old document (clean, no annotations)
          newStreamingDoc,               // New document being streamed
          false                          // isComplete=false: don't mark remaining old lines as deleted
        )

        set({
          brainstormStreamingDocument: newStreamingDoc,
          brainstormDisplayContent: annotatedContent,  // Update display in real-time
          brainstormStreamingLineCount: newLineCount,  // Track for progress indicator
        })
      },

      finishBrainstormDocumentStreaming: (fullDocument) => {
        const state = useInterviewStore.getState()
        console.log('[store] finishBrainstormDocumentStreaming, doc length:', fullDocument.length)
        console.log('[store] cleanContent length:', state.brainstormCleanContent.length)

        // Streaming complete - now compute full diff WITH deletions marked
        const annotatedContent = computeStreamingDiff(
          state.brainstormCleanContent,
          fullDocument,
          true  // isComplete=true: mark remaining old lines as deleted
        )
        console.log('[store] annotatedContent length:', annotatedContent.length)

        // Use NON-PERSISTED brainstormDisplayContent to avoid localStorage race conditions
        // This field is not in partialize(), so persist middleware ignores it entirely
        set({
          brainstormDisplayContent: annotatedContent,  // Transient display - NOT persisted
          brainstormCleanContent: fullDocument,
          brainstormStreamingDocument: '',
          brainstormDocumentStreaming: false,
          brainstormDocumentPending: false,
          brainstormStreamingLineCount: 0,  // Reset line count
        })
        console.log('[store] Set brainstormDisplayContent (transient), length:', annotatedContent.length)
      },

      // Legacy: Instant document update - receives complete annotated document from backend
      updateBrainstormDocumentInstant: (content) =>
        set({
          brainstormContent: content,
          brainstormDocumentPending: false,
        }),

      // Foolscap actions
      updateFoolscapContent: (content) =>
        set({ foolscapContent: content }),

      startFoolscapStreaming: () =>
        set({
          workflowStatus: 'streaming',
          foolscapStreamingContent: { coaching_message: '' },  // Initialize with empty object to track streaming state
          foolscapStreamingThinking: null,
          foolscapIsThinking: false,
          // Show pending overlay immediately when starting foolscap streaming
          foolscapDocumentPending: true,
        }),

      updateFoolscapStreamingContent: (chunk) =>
        set((state) => {
          const current = state.foolscapStreamingContent || { coaching_message: '' }
          const newCoachingMessage = chunk.coaching_message
            ? (current.coaching_message || '') + chunk.coaching_message
            : current.coaching_message
          return {
            foolscapStreamingContent: {
              ...current,
              ...chunk,
              coaching_message: newCoachingMessage,
            },
          }
        }),

      // SET (not append) coaching message - for complete messages that arrive at once
      setFoolscapCoachingMessage: (message) =>
        set((state) => ({
          foolscapStreamingContent: {
            ...state.foolscapStreamingContent,
            coaching_message: message,
          },
        })),

      startFoolscapThinking: () =>
        set({
          foolscapIsThinking: true,
          foolscapStreamingThinking: '',
        }),

      updateFoolscapThinking: (delta) =>
        set((state) => ({
          foolscapStreamingThinking: (state.foolscapStreamingThinking || '') + delta,
        })),

      finishFoolscapThinking: () =>
        set({
          foolscapIsThinking: false,
        }),

      commitFoolscapStreamingMessage: () =>
        set((state) => {
          const content = state.foolscapStreamingContent?.coaching_message || ''
          if (!content) return state
          return {
            brainstormTurns: [
              ...state.brainstormTurns,
              {
                role: 'assistant' as const,
                content,
                thinking: state.foolscapStreamingThinking || undefined,
                timestamp: new Date().toISOString(),
              },
            ],
            workflowStatus: 'idle',
            foolscapStreamingContent: null,
            foolscapStreamingThinking: null,
            foolscapIsThinking: false,
            foolscapDocumentPending: true,
          }
        }),

      finishFoolscapStreaming: (output) =>
        set((state) => {
          const alreadyCommitted = !state.foolscapStreamingContent
          const sessionContext = output.session_context || {}

          // Determine final content to use for foolscap
          // Priority: displayContent (from streaming diff) > output.foolscap_content (clean from backend) > existing
          // Use explicit undefined check since empty string "" is valid (cleared document)
          const contentToUse = state.foolscapDisplayContent !== null
            ? state.foolscapDisplayContent
            : (output.foolscap_content !== undefined ? output.foolscap_content : state.foolscapContent)

          // Determine final content to use for scratchpad
          // Priority: displayContent (from streaming diff) > output.scratchpad_content (clean from backend) > existing
          const scratchpadToUse = state.scratchpadDisplayContent !== null
            ? state.scratchpadDisplayContent
            : (output.scratchpad_content !== undefined ? output.scratchpad_content : state.scratchpadContent)

          const updates: Partial<InterviewState> = {
            foolscapContent: contentToUse,
            // Save clean content for next turn diff computation
            foolscapCleanContent: output.new_foolscap_content || stripAnnotations(contentToUse),
            // Clear transient display content now that we've committed
            foolscapDisplayContent: null,
            foolscapPhase: sessionContext.phase || state.foolscapPhase,
            // Scratchpad content
            scratchpadContent: scratchpadToUse,
            scratchpadCleanContent: output.new_scratchpad_content || stripAnnotations(scratchpadToUse),
            scratchpadDisplayContent: null,
            workflowStatus: 'idle',
            foolscapStreamingContent: null,
            foolscapDocumentPending: false,
            // Clear document streaming state
            foolscapDocumentStreaming: false,
            foolscapStreamingDocument: '',
            foolscapStreamingLineCount: 0,
            // Clear scratchpad streaming state
            scratchpadDocumentStreaming: false,
            scratchpadStreamingDocument: '',
            scratchpadStreamingLineCount: 0,
          }

          // If finalized brainstorm content is provided (first foolscap turn),
          // update brainstormContent with the clean version (annotations stripped)
          if (output.finalized_brainstorm_content) {
            updates.brainstormContent = output.finalized_brainstorm_content
          }

          if (alreadyCommitted) {
            return updates
          }

          const messageContent = output.question
            ? `${output.coaching_message}\n\n${output.question}`
            : output.coaching_message

          return {
            ...updates,
            brainstormTurns: [
              ...state.brainstormTurns,
              {
                role: 'assistant' as const,
                content: messageContent,
                timestamp: new Date().toISOString(),
                // Prefer thinking from output (complete), fall back to streaming thinking
                thinking: output.thinking || state.foolscapStreamingThinking || undefined,
              },
            ],
            // Clear streaming thinking now that it's committed to turn
            foolscapStreamingThinking: null,
          }
        }),

      // Document streaming - line-by-line streaming with frontend diff computation
      startFoolscapDocumentStreaming: () =>
        set({
          foolscapStreamingDocument: '',
          foolscapDocumentStreaming: true,
          foolscapDocumentPending: false,  // Not pending - actively streaming
          foolscapStreamingLineCount: 0,
        }),

      appendFoolscapDocumentLine: (line) =>
        set((state) => {
          const newDocument = state.foolscapStreamingDocument + line
          const lineCount = state.foolscapStreamingLineCount + 1

          // Compute progressive diff as lines stream in
          const annotatedContent = computeStreamingDiff(
            state.foolscapCleanContent,
            newDocument,
            false  // Not complete yet - don't mark remaining old lines as deleted
          )

          return {
            foolscapStreamingDocument: newDocument,
            foolscapStreamingLineCount: lineCount,
            // Update display content with annotated diff (transient, not persisted)
            foolscapDisplayContent: annotatedContent,
          }
        }),

      finishFoolscapDocumentStreaming: (fullDocument) =>
        set((state) => {
          // Compute final diff with all deletions marked
          const annotatedContent = computeStreamingDiff(
            state.foolscapCleanContent,
            fullDocument,
            true  // Complete - mark all unmatched old lines as deleted
          )

          return {
            foolscapDocumentStreaming: false,
            foolscapStreamingDocument: '',
            foolscapStreamingLineCount: 0,
            // Set display content to final annotated version
            foolscapDisplayContent: annotatedContent,
            // Update clean content for next turn's diff computation
            foolscapCleanContent: fullDocument,
          }
        }),

      // Legacy: Document update - receives complete annotated document from backend
      updateFoolscapDocumentInstant: (content) =>
        set({
          foolscapContent: content,
          foolscapDocumentPending: false,
        }),

      // Scratchpad actions
      updateScratchpadContent: (content) =>
        set({ scratchpadContent: content }),

      // Scratchpad streaming - line-by-line streaming with frontend diff computation
      startScratchpadDocumentStreaming: () =>
        set({
          scratchpadStreamingDocument: '',
          scratchpadDocumentStreaming: true,
          scratchpadStreamingLineCount: 0,
        }),

      appendScratchpadDocumentLine: (line) =>
        set((state) => {
          const newDocument = state.scratchpadStreamingDocument + line
          const lineCount = state.scratchpadStreamingLineCount + 1

          // Compute progressive diff as lines stream in
          const annotatedContent = computeStreamingDiff(
            state.scratchpadCleanContent,
            newDocument,
            false  // Not complete yet - don't mark remaining old lines as deleted
          )

          return {
            scratchpadStreamingDocument: newDocument,
            scratchpadStreamingLineCount: lineCount,
            // Update display content with annotated diff (transient, not persisted)
            scratchpadDisplayContent: annotatedContent,
          }
        }),

      finishScratchpadDocumentStreaming: (fullScratchpad) =>
        set((state) => {
          // Compute final diff with complete flag
          const annotatedContent = computeStreamingDiff(
            state.scratchpadCleanContent,
            fullScratchpad,
            true  // Complete - mark all unmatched old lines as deleted
          )

          return {
            scratchpadDocumentStreaming: false,
            scratchpadStreamingDocument: '',
            scratchpadStreamingLineCount: 0,
            // Set display content to final annotated version
            scratchpadDisplayContent: annotatedContent,
            // Update clean content for next turn's diff computation
            scratchpadCleanContent: fullScratchpad,
          }
        }),
    }),
    {
      name: 'interview-store',
      version: 2,  // Bump version to trigger migration
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as Partial<InterviewState>
        if (version < 2) {
          // Migration: Initialize cleanContent fields from content fields
          if (!state.foolscapCleanContent && state.foolscapContent) {
            state.foolscapCleanContent = stripAnnotations(state.foolscapContent)
          }
          if (!state.scratchpadCleanContent && state.scratchpadContent) {
            state.scratchpadCleanContent = stripAnnotations(state.scratchpadContent)
          }
          if (!state.brainstormCleanContent && state.brainstormContent) {
            state.brainstormCleanContent = stripAnnotations(state.brainstormContent)
          }
        }
        return state as InterviewState
      },
      // Persist session state for recovery
      partialize: (state) => ({
        sessionId: state.sessionId,
        sessionName: state.sessionName,
        buildMode: state.buildMode,
        turns: state.turns,
        currentQueries: state.currentQueries,
        narrativeDirection: state.narrativeDirection,
        keyThemes: state.keyThemes,
        phase: state.phase,
        queryResults: state.queryResults,
        totalCards: state.totalCards,
        // Persist brainstorm state
        brainstormMode: state.brainstormMode,
        brainstormContent: state.brainstormContent,
        brainstormCleanContent: state.brainstormCleanContent,
        brainstormPhase: state.brainstormPhase,
        brainstormWorkingDirection: state.brainstormWorkingDirection,
        brainstormKeyThemes: state.brainstormKeyThemes,
        brainstormGaps: state.brainstormGaps,
        brainstormTurns: state.brainstormTurns,
        // Persist foolscap state
        foolscapContent: state.foolscapContent,
        foolscapCleanContent: state.foolscapCleanContent,
        foolscapPhase: state.foolscapPhase,
        // Persist scratchpad state
        scratchpadContent: state.scratchpadContent,
        scratchpadCleanContent: state.scratchpadCleanContent,
      }),
    }
  )
)

// Re-export types from api for convenience
export type { QueryResult, RetrievedCard }
